import SoftButton from "components/SoftButton";
import Icon from "@mui/material/Icon";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import { CircularProgress, IconButton } from "@mui/material";
import { ThemedXIcon } from "assets/CXFIcons/Static";
import { ThemedTrashIcon } from "assets/CXFIcons/Triggers";
import { useEffect } from "react";
import { usePortalFlows } from "hooks/usePortalFlows";
import { useResourceDependencies } from "hooks/useResourceDependencies";

export const TrashComponentDialog = ({
  open,
  onClose,
  onAccept,
  component,
  deletePermanentlyCallback
}) => {
  const { flows, handleFetchFlows, areFlowsLoaded, isLoadingPortalFlows } =
    usePortalFlows(false);
  const {
    dependencies: connectorFlowIds,
    isLoading: isLoadingDependencies,
    hasFetched: hasFetchedDependencies,
    error: dependenciesError
  } = useResourceDependencies({
    sourceId: component?.id,
    direction: "reverse",
    targetResourceType: "flow",
    shouldFetch: open && Boolean(component?.id)
  });
  const flowsUsingTheConnector = connectorFlowIds
    .map((flowId) => flows.find((flow) => flow.id === flowId))
    .filter(Boolean);
  const isLoadingFlowNames = connectorFlowIds.length > 0 && (!areFlowsLoaded || isLoadingPortalFlows);
  const isCheckingDependencies =
    open && component?.id && (!hasFetchedDependencies || isLoadingDependencies);
  const isCheckingUsage = isCheckingDependencies || isLoadingFlowNames;
  const hasCheckFailed = Boolean(dependenciesError);
  const isConnectorUsed = flowsUsingTheConnector.length > 0;

  useEffect(() => {
    if (!open) return;

    handleFetchFlows();
  }, [open]);

  const handleAccept = async () => {
    if (isCheckingUsage || hasCheckFailed) return;

    const shouldClose = await onAccept();

    if (shouldClose !== false) {
      onClose();
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth={true} maxWidth="sm">
        <DialogTitle
          sx={{
            marginBottom: "20px",
            padding: "32px 24px 0 24px !important",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
          className="white-to-dark"
          data-testid="form-title"
        >
          <SoftBox display="flex" alignItems="center">
            <ThemedTrashIcon sx={{ fontSize: "16px !important", marginRight: "16px" }} />
            Trash
          </SoftBox>
          <IconButton onClick={onClose}>
            <ThemedXIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            padding: "0 32px 24px",
          }}
        >
          <SoftBox
            className="border-top"
            sx={{
              paddingTop: "20px",
            }}
          >
            {isCheckingUsage ? (
              <SoftBox
                sx={{
                  alignItems: "center",
                  display: "flex",
                  gap: "12px",
                  marginBottom: "20px",
                }}
              >
                <CircularProgress size={20} />
                <SoftTypography color="text" sx={{ fontSize: "14px" }}>
                  Checking flows using this connector...
                </SoftTypography>
              </SoftBox>
            ) : hasCheckFailed ? (
              <SoftBox
                className="warning-label"
                sx={{
                  alignItems: "center",
                  backgroundColor: "#FFEDED",
                  border: "0.5px solid #E53E3E",
                  display: "flex",
                  borderRadius: "9px",
                  color: "#E53E3E",
                  height: "59px",
                  justifyContent: "center",
                  marginBottom: "20px",
                }}
              >
                <Icon sx={{ fontSize: "16px !important", marginRight: "16px" }}>warning</Icon>
                <SoftTypography
                  sx={{
                    color: "#E53E3E",
                    fontSize: "14px",
                    fontWeight: 600,
                  }}
                >
                  Usage could not be verified. Please try again before deleting.
                </SoftTypography>
              </SoftBox>
            ) : isConnectorUsed && flowsUsingTheConnector.length > 0 ? (
              <>
                <SoftBox
                  className="warning-label"
                  sx={{
                    alignItems: "center",
                    backgroundColor: "#FFEDED",
                    border: "0.5px solid #E53E3E",
                    display: "flex",
                    borderRadius: "9px",
                    color: "#E53E3E",
                    height: "59px",
                    justifyContent: "center",
                    marginBottom: "20px",
                  }}
                >
                  <Icon sx={{ fontSize: "16px !important", marginRight: "16px" }}>warning</Icon>
                  <SoftTypography
                    sx={{
                      color: "#E53E3E",
                      fontSize: "14px",
                      fontWeight: 600,
                    }}
                  >
                    Warning: This connector is actively being used
                  </SoftTypography>
                </SoftBox>
                <SoftTypography
                  className="white-to-dark"
                  sx={{
                    fontSize: "14px",
                    fontWeight: 700,
                    marginBottom: "8px",
                  }}
                >
                  Flows using this connector:
                </SoftTypography>
                <SoftBox
                  sx={{
                    maxHeight: "200px",
                    overflowY: "auto",
                    border: "1px solid #E6E6E6",
                    borderRadius: "8px",
                    padding: "8px",
                  }}
                >
                  {flowsUsingTheConnector.map((flow) => (
                    <SoftTypography
                      key={flow.id}
                      sx={{
                        fontSize: "14px",
                        marginBottom: "4px",
                      }}
                    >
                      {flow.name}
                    </SoftTypography>
                  ))}
                </SoftBox>
              </>
            ) : (
              <>
                <SoftTypography
                  className="white-to-dark"
                  sx={{
                    fontSize: "14px",
                    fontWeight: 700,
                    marginBottom: "8px",
                  }}
                >
                  Delete Connector
                </SoftTypography>
                <SoftTypography
                  color="text"
                  sx={{
                    fontSize: "14px",
                    marginBottom: "16px",
                  }}
                >
                  You can move this connector to trash and retrieve it before it is permanently
                  deleted after 30 days.
                </SoftTypography>
                <SoftButton
                  sx={{
                    marginBottom: "24px",
                    width: "100%",
                  }}
                  variant="outlined"
                  color="info"
                  onClick={handleAccept}
                  disabled={isConnectorUsed || isCheckingUsage || hasCheckFailed}
                >
                  move to trash
                </SoftButton>
                <button
                  style={{
                    textDecoration: "underline",
                    maxWidth: "max-content",
                    margin: "0 auto",
                    background: "none",
                    border: "none",
                    display: "block",
                    fontWeight: "700",
                    cursor: "pointer",
                  }}
                  onClick={deletePermanentlyCallback}
                  disabled={isConnectorUsed || isCheckingUsage || hasCheckFailed}
                >
                  I wish to permanently delete
                </button>
              </>
            )}
          </SoftBox>
        </DialogContent>
      </Dialog>
    </>
  );
};
